import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { DeleteOutlined, EditOutlined, TeamOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/react-hooks";
import * as Sentry from "@sentry/browser";
import { Alert, Button, Dropdown, Menu, message, Modal } from "antd";
import { connect } from "formik";
import gql from "fraql";
import find from "lodash/find";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import omit from "lodash/omit";
import { getCookieValue } from "../../../../../utils/cookies";
import { serverErrorStatus } from "../../../../../utils/graphqlErrors";
import useCurrentUser from "../../../../../utils/useCurrentUser";
import { useGetUserHasPermission, PERMISSION } from "../../../../../utils/useGetUserHasPermission";
import { useRegContext } from "../../../../../utils/useRegContext";
import { useAuth0 } from "../../../../Auth0";
import ButtonMore from "../../../../ButtonMore";
import DrawerCreateTeam from "../../../../DrawerCreateTeam";
import DrawerUpdateTeam from "../../../../DrawerUpdateTeam";
import FormFieldTeam, { isFull } from "../../../../FormFields/FormFieldTeam";
import FormFieldTeamPosition from "../../../../FormFields/FormFieldTeamPosition";
import TeamQualificationScoreNotProvidedAlert from "../../../Competitions/TeamQualificationScoreNotProvidedAlert";
import ViewTeamDrawer from "../../../Competitions/ViewTeamDrawer/ViewTeamDrawer";
import TeamInvitationCard from "./TeamInvitationCard";
import "./RegistrationStepCompetitionsFormFieldTeam.scss";

const GET_TEAM_BY_ID = gql`
  query RegistrationStepCompetitionsFormFieldTeam_GetTeamById($TeamId: String!) {
    Team_by_pk(id: $TeamId) {
      id
      name
      qualificationScore
      calculatedAgeGroupId
      teamManagerId
      Competition {
        id
        qualificationScoreType
        qualificationScoreRequired
        teamQualificationScoreDetermination
        maxTeamMembers
      }
      TeamPeople {
        PersonId
      }
      CreatedBy {
        id
      }
      TeamPeople_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`;

const GET_INVITED_TEAMS_FOR_USER_IN_COMPETITION = gql`
  query RegistrationStepCompetitionsFormFieldTeam_GetInvitedTeamsForUserInCompetition(
    $UserEmail: String!
    $CompetitionId: String!
    $TeamId: String
  ) {
    getInvitedTeamIdsForUserInCompetition(UserEmail: $UserEmail, CompetitionId: $CompetitionId, TeamId: $TeamId) {
      affected_rows
      TeamIds
      teams {
        id
        name
        teamManagerName
        isFull
        competitionId
        competitionName
        calculatedAgeGroupId
        calculatedAgeGroupName
      }
    }
  }
`;

const DELETE_TEAM = gql`
  mutation RegistrationStepCompetitionsFormFieldTeam_DeleteTeam($TeamId: ID!) {
    deleteTeam(TeamId: $TeamId) {
      affected_rows
    }
  }
`;

const GET_QUERY_ROOT_FIELDS = gql`
  query RegistrationStepCompetitionsFormFieldTeam_GetQueryRootFields {
    __type(name: "query_root") {
      fields {
        name
      }
    }
  }
`;

function RegistrationStepCompetitionsFormFieldTeam(props) {
  const {
    name,
    disabled,
    competitionId,
    competitionGender,
    registrantsCanCreateTeams,
    registrantsCanCreateTeamsRoles,
    managersCanCreateTeams,
    teamSelectionRequired,
    teamPositionRequired,
    formik: { setFieldValue, setFieldTouched, values },
  } = props;

  const teamFieldName = `${name}.team`;
  const teamFieldValue = get(values, teamFieldName, undefined);

  const teamPositionFieldName = `${name}.teamPosition`;

  const currentUser = useCurrentUser();
  const { user } = useAuth0();
  const userEmail = get(user, "email", null);
  const invitedTeamIdFromCookie = useMemo(() => getCookieValue("InvitedToTeam"), []);
  const regContext = useRegContext();
  const regId = get(regContext, "regContextData.RegId");
  const userRegSubmissionParticipationRoleId = get(regContext, "regContextData.RegSubmission.RoleId", null);
  const hasFullAccessToManageTeam = useGetUserHasPermission(PERMISSION.MANAGE_TEAM);
  const hasConditionalAccessToManageTeam = useGetUserHasPermission(PERMISSION.MANAGE_TEAM_CONDITIONALLY);
  const hasRegistrantAccessToManageTeam = useGetUserHasPermission(PERMISSION.MANAGE_TEAM_REGISTRANT);
  const hasPermissionToShowViewTeamButton = useGetUserHasPermission(PERMISSION.VIEW_TEAM);

  const [isDrawerCreateTeamVisible, setIsDrawerCreateTeamVisible] = useState(false);
  const [isDrawerUpdateTeamVisible, setIsDrawerUpdateTeamVisible] = useState(false);
  const [isViewTeamDrawerVisible, setIsViewTeamDrawerVisible] = useState(false);
  const [viewTeamDrawerInitialTabKey, setViewTeamDrawerInitialTabKey] = useState(undefined);

  const ageGroupId = get(values, `${name}.ageGroup`, null);

  const { data: selectedTeamData, refetch: selectedTeamRefetch } = useQuery(GET_TEAM_BY_ID, {
    variables: { TeamId: teamFieldValue || "" },
    skip: isEmpty(teamFieldValue),
  });

  const { data: queryRootFieldsData, error: queryRootFieldsError } = useQuery(GET_QUERY_ROOT_FIELDS);

  const hasGetInvitedTeamIdsForUserInCompetitionAction = useMemo(() => {
    if (queryRootFieldsError) {
      return false;
    }

    const queryRootFields = get(queryRootFieldsData, "__type.fields", []);

    return !!find(queryRootFields, { name: "getInvitedTeamIdsForUserInCompetition" });
  }, [queryRootFieldsData, queryRootFieldsError]);

  const [joinErrors, setJoinErrors] = useState({});

  const { data: invitedTeamsResponseData, loading: invitedTeamsLoading } = useQuery(
    GET_INVITED_TEAMS_FOR_USER_IN_COMPETITION,
    {
      variables: {
        UserEmail: userEmail || "",
        CompetitionId: competitionId,
      },
      skip: isEmpty(userEmail) || !hasGetInvitedTeamIdsForUserInCompetitionAction,
      fetchPolicy: "network-only",
    },
  );

  const invitedTeams = useMemo(() => {
    return get(invitedTeamsResponseData, "getInvitedTeamIdsForUserInCompetition.teams", []);
  }, [invitedTeamsResponseData]);

  const isCookieInviteExpired = useMemo(() => {
    if (isEmpty(invitedTeamIdFromCookie) || invitedTeamsLoading) {
      return false;
    }
    return !invitedTeams.some(t => t.id === invitedTeamIdFromCookie);
  }, [invitedTeamIdFromCookie, invitedTeams, invitedTeamsLoading]);

  const handleJoinTeam = useCallback(
    teamToJoin => {
      if (teamToJoin.isFull) {
        setJoinErrors(prev => ({
          ...prev,
          [teamToJoin.id]: `Cannot join ${teamToJoin.name} because the team is full.`,
        }));
        return;
      }

      const personGender = get(regContext, "regContextData.Person.gender", null);
      if (
        competitionGender &&
        !["ANY", "COED", "OPEN"].includes(competitionGender.toUpperCase()) &&
        personGender &&
        competitionGender.toLowerCase() !== personGender.toLowerCase()
      ) {
        setJoinErrors(prev => ({
          ...prev,
          [teamToJoin.id]: `You are not eligible to join ${teamToJoin.name} due to gender requirements for this competition.`,
        }));
        return;
      }

      setJoinErrors(prev => {
        const next = { ...prev };
        delete next[teamToJoin.id];
        return next;
      });

      setFieldValue(teamFieldName, teamToJoin.id);
      setFieldTouched(teamFieldName, true);
    },
    [competitionGender, regContext, setFieldTouched, setFieldValue, teamFieldName],
  );

  const handleResetTeam = useCallback(() => {
    setJoinErrors({});
    setFieldValue(teamFieldName, undefined);
    setFieldTouched(teamFieldName, true);
  }, [setFieldTouched, setFieldValue, teamFieldName]);

  const teamSelected = useMemo(() => {
    if (!teamFieldValue) {
      return undefined;
    }

    const result = get(selectedTeamData, "Team_by_pk", undefined);

    if (result && isFull(result)) {
      return undefined;
    }

    return result;
  }, [selectedTeamData, teamFieldValue]);

  const [deleteTeam] = useMutation(DELETE_TEAM);

  const handleCloseDrawerCreateTeam = useCallback(() => {
    setIsDrawerCreateTeamVisible(false);
  }, [setIsDrawerCreateTeamVisible]);

  const handleCloseDrawerUpdateTeam = useCallback(() => {
    setIsDrawerUpdateTeamVisible(false);
  }, [setIsDrawerUpdateTeamVisible]);

  const handleMutationsTeamSuccess = useCallback(
    async newTeamId => {
      setIsDrawerCreateTeamVisible(false);
      setIsDrawerUpdateTeamVisible(false);

      if (selectedTeamRefetch) {
        await selectedTeamRefetch();
      }

      setFieldValue(teamFieldName, newTeamId);
      setFieldTouched(teamFieldName);
    },
    [
      teamFieldName,
      setIsDrawerCreateTeamVisible,
      setIsDrawerUpdateTeamVisible,
      selectedTeamRefetch,
      setFieldValue,
      setFieldTouched,
    ],
  );

  const handleCreateTeamSuccess = useCallback(
    async newTeamId => {
      await handleMutationsTeamSuccess(newTeamId);
    },
    [handleMutationsTeamSuccess],
  );

  const handleUpdateTeamSuccess = useCallback(
    async newTeamId => {
      await handleMutationsTeamSuccess(newTeamId);
    },
    [handleMutationsTeamSuccess],
  );

  const handleViewTeamClick = useCallback(
    initialTabKey => {
      setViewTeamDrawerInitialTabKey(initialTabKey);
      setIsViewTeamDrawerVisible(true);
    },
    [setViewTeamDrawerInitialTabKey, setIsViewTeamDrawerVisible],
  );

  const handleInviteTeamMembersClick = useCallback(() => {
    handleViewTeamClick("invited");
  }, [handleViewTeamClick]);

  const handleCloseViewTeamDrawer = useCallback(() => {
    setIsViewTeamDrawerVisible(false);
    setViewTeamDrawerInitialTabKey(undefined);
  }, [setIsViewTeamDrawerVisible, setViewTeamDrawerInitialTabKey]);

  const handleDeleteTeam = useCallback(() => {
    const teamName = get(teamSelected, "name", "");

    if (!teamName) {
      return;
    }

    const content = `Are you sure you want to delete the team "${teamName}"? If any person has chosen it in their registration submission, it will not be deleted.`;

    const modal = Modal.confirm({
      title: "Delete team from competition",
      content,
      okText: "Ok",
      async onOk() {
        modal.update({
          okButtonProps: {
            loading: true,
          },
          cancelButtonProps: {
            disabled: true,
          },
        });

        try {
          await deleteTeam({
            variables: { TeamId: teamSelected.id },
          });

          await handleMutationsTeamSuccess("");

          message.success("Team deleted.");
        } catch (error) {
          console.error(error);

          const errorStatus = serverErrorStatus(error);

          const description = get(
            errorStatus,
            "description",
            "There was an error processing your request. Please try again later.",
          );

          Modal.error({
            title: "Team not deleted",
            content: description,
          });

          Sentry.captureException(error);
        }
      },
      onCancel() {},
    });
  }, [deleteTeam, teamSelected, handleMutationsTeamSuccess]);

  const userCanManageTeam = useMemo(() => {
    if (hasFullAccessToManageTeam) {
      return true;
    }

    const teamCreatorId = get(teamSelected, "CreatedBy.id", null);

    if (isNull(teamCreatorId)) {
      return false;
    }

    return (
      (hasConditionalAccessToManageTeam || hasRegistrantAccessToManageTeam) && get(currentUser, "id") === teamCreatorId
    );
  }, [
    hasFullAccessToManageTeam,
    hasConditionalAccessToManageTeam,
    hasRegistrantAccessToManageTeam,
    currentUser,
    teamSelected,
  ]);

  const showInviteTeamMembersButton = useMemo(() => {
    return !isUndefined(teamSelected) && userCanManageTeam;
  }, [teamSelected, userCanManageTeam]);

  const dropdownMarkup = useMemo(() => {
    if (isUndefined(teamSelected)) {
      return null;
    }

    const menuItems = [];

    if (hasPermissionToShowViewTeamButton) {
      menuItems.push(
        <Menu.Item key="0" onClick={handleViewTeamClick}>
          <TeamOutlined />
          View Team
        </Menu.Item>,
      );
    }

    if (userCanManageTeam) {
      menuItems.push(
        <Menu.Item key="1" onClick={() => setIsDrawerUpdateTeamVisible(true)}>
          <EditOutlined />
          Edit
        </Menu.Item>,
        <Menu.Item key="2" onClick={handleDeleteTeam}>
          <DeleteOutlined />
          Delete
        </Menu.Item>,
      );
    }

    if (isEmpty(menuItems)) {
      return null;
    }

    const menu = <Menu theme="dark">{menuItems}</Menu>;

    return (
      <>
        <Dropdown overlay={menu} trigger={["click"]}>
          <ButtonMore className="form-field-team__button" />
        </Dropdown>

        <DrawerUpdateTeam
          isVisible={isDrawerUpdateTeamVisible && !disabled}
          handleClose={handleCloseDrawerUpdateTeam}
          handleSuccess={handleUpdateTeamSuccess}
          teamId={teamSelected.id}
        />

        <ViewTeamDrawer
          visible={isViewTeamDrawerVisible}
          teamId={teamFieldValue}
          regId={regId}
          handleClose={handleCloseViewTeamDrawer}
          initialTabKey={viewTeamDrawerInitialTabKey}
        />
      </>
    );
  }, [
    handleDeleteTeam,
    teamSelected,
    setIsDrawerUpdateTeamVisible,
    disabled,
    isDrawerUpdateTeamVisible,
    handleCloseDrawerUpdateTeam,
    isViewTeamDrawerVisible,
    teamFieldValue,
    regId,
    handleCloseViewTeamDrawer,
    viewTeamDrawerInitialTabKey,
    hasPermissionToShowViewTeamButton,
    handleViewTeamClick,
    userCanManageTeam,
    handleUpdateTeamSuccess,
  ]);

  const canCreateTeam = useMemo(() => {
    const allowedRegistrantRoleIds = registrantsCanCreateTeamsRoles
      .map(roleItem => get(roleItem, "Role.id", get(roleItem, "RoleId", get(roleItem, "roleId", null))))
      .filter(roleId => !isNull(roleId) && !isUndefined(roleId));

    const hasMatchingRegistrantRole = allowedRegistrantRoleIds.includes(userRegSubmissionParticipationRoleId);

    const canCreateFromRegistrantRole =
      registrantsCanCreateTeams && hasRegistrantAccessToManageTeam && hasMatchingRegistrantRole;

    if (hasFullAccessToManageTeam) {
      return true;
    }

    if (hasConditionalAccessToManageTeam && managersCanCreateTeams) {
      return true;
    }

    if (canCreateFromRegistrantRole) {
      return true;
    }

    return false;
  }, [
    hasFullAccessToManageTeam,
    hasConditionalAccessToManageTeam,
    hasRegistrantAccessToManageTeam,
    managersCanCreateTeams,
    registrantsCanCreateTeams,
    registrantsCanCreateTeamsRoles,
    userRegSubmissionParticipationRoleId,
  ]);

  return (
    <>
      <>
        <div className="form-field-team">
          <div className="form-field-team__container">
            <div className="form-field-team__field">
              <FormFieldTeam
                {...omit(props, ["formik"])}
                meta={{
                  required: teamSelectionRequired,
                  label: "Find your team",
                  placeholder: "Search by team name or Team Manager name",
                  allowClear: true,
                }}
                competitionId={competitionId}
                ageGroupId={ageGroupId}
                name={teamFieldName}
                teamSelected={teamSelected}
                personId={regContext.regContextData.PersonId}
              />

              <TeamInvitationCard
                invitedTeams={invitedTeams}
                selectedTeamId={teamFieldValue}
                onJoin={handleJoinTeam}
                onReset={handleResetTeam}
                disabled={disabled}
                joinErrors={joinErrors}
              />

              {isCookieInviteExpired && (
                <Alert
                  message="This team invitation is no longer active or was sent to a different email address."
                  type="warning"
                  showIcon
                  className="form-field-team__invited-alert"
                />
              )}
            </div>

            {dropdownMarkup}
          </div>
          {teamSelected !== undefined && (
            <div className="form-field-team__position">
              <FormFieldTeamPosition
                {...omit(props, ["formik"])}
                meta={{
                  required: teamPositionRequired,
                  label: "Position",
                  placeholder: "Select a position",
                  allowClear: true,
                }}
                queryOptions={{ variables: { CompetitionId: competitionId } }}
                name={teamPositionFieldName}
                hideOnEmpty
                hideOnLoading
              />
            </div>
          )}
        </div>

        <TeamQualificationScoreNotProvidedAlert style={{ marginTop: 15 }} team={teamSelected} />
      </>

      {(canCreateTeam || showInviteTeamMembersButton) && (
        <div className="registration-step-competition-form-field-team__actions">
          {showInviteTeamMembersButton && (
            <Button
              className="registration-step-competition-form-field-team__action-button"
              onClick={handleInviteTeamMembersClick}
              disabled={disabled}
            >
              Invite Team Members
            </Button>
          )}

          {canCreateTeam && (
            <Button
              className="registration-step-competition-form-field-team__action-button"
              onClick={() => setIsDrawerCreateTeamVisible(true)}
              disabled={disabled}
            >
              Create New Team
            </Button>
          )}
        </div>
      )}

      <DrawerCreateTeam
        isVisible={isDrawerCreateTeamVisible && !disabled}
        handleClose={handleCloseDrawerCreateTeam}
        handleSuccess={handleCreateTeamSuccess}
        competitionId={competitionId}
        regId={regId}
      />
    </>
  );
}

RegistrationStepCompetitionsFormFieldTeam.propTypes = {
  name: PropTypes.string.isRequired,
  submitCount: PropTypes.number.isRequired,
  disabled: PropTypes.bool.isRequired,
  competitionId: PropTypes.string.isRequired,
  registrantsCanCreateTeams: PropTypes.bool.isRequired,
  registrantsCanCreateTeamsRoles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      Role: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ).isRequired,
  managersCanCreateTeams: PropTypes.bool.isRequired,
  teamSelectionRequired: PropTypes.bool.isRequired,
  teamPositionRequired: PropTypes.bool.isRequired,
  formik: PropTypes.shape({
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    values: PropTypes.object,
  }).isRequired,
  readOnly: PropTypes.bool,
  competitionGender: PropTypes.string,
};

RegistrationStepCompetitionsFormFieldTeam.defaultProps = {
  readOnly: false,
  competitionGender: null,
};

export default connect(RegistrationStepCompetitionsFormFieldTeam);
